// @file Microsoft OneDrive Uploads store
import { trackEvent } from '@@/bits/analytics'
import { captureException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { UploadMicrosoftOneDriveFileJob } from '@@/bits/microsoft_onedrive_file_uploader'
import { ONE_MB } from '@@/bits/numbers'
import type { UploadOwnerParams } from '@@/bits/uploader'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { MicrosoftOneDriveUploadType } from '@@/pinia/microsoft_onedrive_picker_store'
import type { MicrosoftOneDriveUploadResponse } from '@@/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

const useMicrosoftOneDriveUploadsStore = defineStore('microsoftOneDriveUploads', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const progressTimerById = ref<Record<string, ReturnType<typeof setInterval>>>({})

  function startUploadJob({
    uploadKey,
    fileId,
    fileName,
    fileType,
    fileSize,
    driveId,
    endpoint,
    accessToken,
    uploadType,
    webUrl,
    wallId,
    libraryId,
    tenantId,
    classroomKind,
    classroomId,
    onStart,
    onError,
    onProgress,
    onSuccess,
  }: {
    uploadKey: string
    fileId: string
    fileName: string
    fileType: string
    fileSize: number
    driveId: string
    endpoint: string
    accessToken: string
    uploadType: MicrosoftOneDriveUploadType
    webUrl?: string // only used for URL uploads, for file uploads we get the pre-allocated, expected URL of the cloud storage upload destination
    onStart: (url: string) => void
    onError?: (response: MicrosoftOneDriveUploadResponse) => void
    onProgress?: (progress: number) => void
    onSuccess?: (response: MicrosoftOneDriveUploadResponse) => void
  } & UploadOwnerParams): void {
    const uploadJob = new UploadMicrosoftOneDriveFileJob({
      fileName,
      fileType,
      fileSize,
      wallId,
      libraryId,
      tenantId,
      classroomKind,
      classroomId,
    })

    uploadJob.on('error', (response: MicrosoftOneDriveUploadResponse) => {
      onUploadError({ uploadKey, response })
      onError?.(response)
    })
    uploadJob.on('progress', (progress: number) => onProgress?.(progress))

    // Helper function to handle upload completion
    const handleUploadDone = (response: MicrosoftOneDriveUploadResponse): void => {
      onFileUploadDone({ uploadKey, response })
      // set progress to 100%, overriding any existing "fake" progress
      onProgress?.(1)
      onSuccess?.(response)
    }

    // For URL uploads, emit the webUrl since we know it immediately
    if (uploadType === MicrosoftOneDriveUploadType.Url) {
      const reason = fileType === 'one' ? 'OneNote file' : 'File larger than max size'
      if (fileType === 'one') {
        trackEvent('Microsoft OneDrive', 'OneNote file, only uploading URL', null, fileSize, {
          fileId,
          driveId,
          endpoint,
          webUrl,
        })
      } else {
        trackEvent('Microsoft OneDrive', 'File larger than max size, only uploading URL', null, fileSize, {
          fileId,
          driveId,
          endpoint,
          webUrl,
        })
      }

      if (!webUrl) {
        // Handle case where webUrl is missing for URL uploads
        onError?.({
          url: '',
          errors: [
            {
              code: 'MISSING_WEB_URL',
              status: '400',
              title: 'Microsoft OneDrive: Upload URL flow, missing webUrl',
              detail: 'Microsoft OneDrive: Upload URL flow, missing webUrl',
            },
          ],
        })
        return
      }
      // emit the webUrl as onStart
      onStart?.(webUrl)

      // Register event listener right before starting upload
      uploadJob.on('done', (response: MicrosoftOneDriveUploadResponse) => {
        handleUploadDone(response)
      })

      // then start uploading the file to our cloud storage
      void uploadJob.uploadMicrosoftOneDriveUrl({
        fileId,
        driveId,
        endpoint,
        accessToken,
      })
    }
    // For file uploads, we'll emit the pre-allocated, expected URL of the cloud storage upload destination
    // then, start downloading and uploading the file to our cloud storage, creating a static copy of the file
    else if (uploadType === MicrosoftOneDriveUploadType.File) {
      trackEvent('Microsoft OneDrive', 'File smaller than max size, downloading and uploading file', null, fileSize, {
        fileId,
        driveId,
        endpoint,
      })

      uploadJob
        .expectedUploadUrl({ fileId, driveId, endpoint, accessToken })
        .then((expectedUrl) => {
          // emit the expected URL as onStart
          onStart?.(expectedUrl)

          // Register event listener right before starting upload
          uploadJob.on('done', (response: MicrosoftOneDriveUploadResponse) => {
            handleUploadDone(response)
          })

          // then start uploading the file to our cloud storage
          void uploadJob.uploadMicrosoftOneDriveFile({
            fileId,
            driveId,
            endpoint,
            accessToken,
          })
        })
        .catch((error) => {
          captureException(error)
          // The download never starts, so stop the fake progress started below.
          clearInterval(progressTimerById.value[uploadKey])
          onError?.({
            url: '',
            errors: [
              {
                code: 'FAILED_TO_GET_EXPECTED_URL',
                status: '400',
                title: 'Microsoft OneDrive: Upload File flow, failed to get expected URL',
                detail: 'Microsoft OneDrive: Upload File flow, failed to get expected URL',
              },
            ],
          })
        })
    }
    fakeUploadProgress({ uploadKey, fileSize, onProgress })
  }

  function onUploadError({
    uploadKey,
    response,
  }: {
    uploadKey: string
    response: MicrosoftOneDriveUploadResponse
  }): void {
    trackEvent('Microsoft OneDrive', 'Upload Error', null, null, { uploadKey, error: response.errors })

    const currentProgressTimer = progressTimerById.value[uploadKey]
    if (currentProgressTimer != null) {
      clearInterval(currentProgressTimer)
    }

    globalSnackbarStore.setSnackbar({
      message: __('Sorry, there was a problem uploading your file. Please try again.'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function onFileUploadDone({
    uploadKey,
    response,
  }: {
    uploadKey: string
    response: MicrosoftOneDriveUploadResponse
  }): void {
    if (response.errors != null) {
      onUploadError({ uploadKey, response })
      return
    }

    const currentProgressTimer = progressTimerById.value[uploadKey]
    if (currentProgressTimer != null) {
      clearInterval(currentProgressTimer)
    }
  }

  // TODO: PRO-23458 this is very similar to the function in google_drive_uploads.ts, can be consolidated
  function fakeUploadProgress({
    uploadKey,
    fileSize,
    onProgress,
  }: {
    uploadKey: string
    fileSize: number
    onProgress?: (progress: number) => void
  }): void {
    // Initialize upload progress at initial value so that it doesn't start at 0
    const initialUploadProgress = 0.05
    onProgress?.(initialUploadProgress)

    // Calculate expected time and upload based on that
    const expectedTime = fileSize !== null ? fileSize / (4 * ONE_MB) + 4 : 1

    let timeSpent = 0
    const progressTimer = setInterval(() => {
      if (timeSpent < expectedTime) {
        const uploadProgress = initialUploadProgress + (timeSpent / expectedTime) * 0.7
        onProgress?.(uploadProgress)
        timeSpent += 1
      }
    }, 1000)

    progressTimerById.value[uploadKey] = progressTimer
  }

  return {
    startUploadJob,
  }
})
export { useMicrosoftOneDriveUploadsStore }
